import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';

// Generic audit record type
interface GenericAuditRecord {
  auditId?: string;
  operation?: string;
  auditTimestamp?: string;
  changedByName?: string;
  changedFields?: string[];
  [key: string]: unknown; // Allow dynamic fields like employeeSnapshot, childSnapshot, etc.
}

// Generic audit response type
interface GenericAuditResponse<T extends GenericAuditRecord = GenericAuditRecord> {
  records?: T[];
  page?: number;
  limit?: number;
  total?: number;
  pages?: number;
}

// Configuration for creating entity-specific audit hooks
interface AuditHookConfig<T extends GenericAuditRecord> {
  entityType: string;
  apiFunction: (entityId: string, page: number, limit: number) => Promise<GenericAuditResponse<T>>;
}

// Factory function to create entity-specific audit query keys
export function createAuditQueryKeys(entityType: string) {
  return {
    all: [entityType, 'audit'] as const,
    history: (id: string) => [entityType, id, 'audit'] as const,
    // orgId sits inside the trailing object so the history(id) prefix still
    // invalidates every page of every org.
    historyWithPagination: (id: string, page?: number, limit?: number, orgId?: string) =>
      [entityType, id, 'audit', { orgId, page, limit }] as const,
    fieldHistory: (id: string, field: string) => 
      [entityType, id, 'audit-field-history', field] as const,
    recentChanges: (id: string) => 
      [entityType, id, 'audit-recent-changes'] as const,
  };
}

// Factory function to create entity-specific audit history hook
export function createAuditHistoryHook<T extends GenericAuditRecord>(
  config: AuditHookConfig<T>
) {
  const queryKeys = createAuditQueryKeys(config.entityType);

  return function useEntityAuditHistory(
    entityId: string,
    options?: {
      page?: number;
      limit?: number;
      enabled?: boolean;
      isTabActive?: boolean;
      // Active org, for tenant cache isolation. Callers that pass it get a
      // separate cache entry per org.
      orgId?: string;
    }
  ): UseQueryResult<GenericAuditResponse<T>> {
    const { page = 1, limit = 20, enabled = true, isTabActive = false, orgId } = options || {};

    return useQuery<GenericAuditResponse<T>>({
      queryKey: queryKeys.historyWithPagination(entityId, page, limit, orgId),
      queryFn: async () => {
        const result = await config.apiFunction(entityId, page, limit);
        return result;
      },
      enabled: enabled && !!entityId,
      // Dynamic stale time: shorter when tab is active for fresher data
      staleTime: isTabActive ? 5000 : 30000, // 5 seconds when active, 30 seconds when inactive
      // Refetch when tab becomes active (window focus)
      refetchOnWindowFocus: isTabActive,
      retry: (failureCount, error) => {
        // Don't retry on 401/403 errors
        if (error instanceof Error && (
          error.message.includes('401') || 
          error.message.includes('403') || 
          error.message.includes('Unauthorized')
        )) {
          return false;
        }
        return failureCount < 3;
      }
    });
  };
}

// Utility function to invalidate all audit queries for an entity
export function createInvalidateAuditQueries(entityType: string) {
  const queryKeys = createAuditQueryKeys(entityType);
  
  return function invalidateEntityAuditQueries(
    queryClient: QueryClient, 
    entityId: string
  ) {
    queryClient.invalidateQueries({ queryKey: queryKeys.history(entityId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.recentChanges(entityId) });
    // Note: field history queries will be invalidated by the above due to query key structure
  };
}

// Example usage for creating child-specific audit hooks:
/*
// In useChildAudit.ts:
import { api } from '@/api';
import { createAuditHistoryHook, createInvalidateAuditQueries } from './useGenericAudit';

export const useChildAuditHistory = createAuditHistoryHook({
  entityType: 'children',
  apiFunction: (id, page, limit) => api.children.getApiV1ChildrenHistory(id, page, limit),
});

export const invalidateChildAuditQueries = createInvalidateAuditQueries('children');
*/